import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCheck, FaSpinner, FaExclamationTriangle, FaSync } from 'react-icons/fa';
import { useNotification } from '../../context/NotificationContext';
import NotificationItem from './NotificationItem';
// Notification categories import removed since filters are hidden
import styles from './NotificationDropdown.module.css';

/**
 * NotificationDropdown Component
 * 
 * Simplified notification dropdown with:
 * - Mark all as read functionality
 * - Real-time updates
 * - Loading states
 * - Error handling
 * - Pagination support
 * - Keyboard navigation
 */
const NotificationDropdown = ({ onClose, onNotificationClick }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  
  const navigate = useNavigate();
  
  const {
    fetchNotifications,
    markAllAsRead,
    unreadCount,
    refreshNotifications
  } = useNotification();

  /**
   * Load notifications based on current filter
   */
  const loadNotifications = useCallback(async (offset = 0, append = false) => {
    try {
      if (offset === 0) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }
      
      const params = {
        limit: 10,
        offset
        // Always show all notifications since filters are removed
      };
      
      const response = await fetchNotifications(params);
      const newNotifications = response.notifications || [];
      
      if (append) {
        setNotifications(prev => [...prev, ...newNotifications]);
      } else {
        setNotifications(newNotifications);
      }
      
      setHasMore(response.pagination?.hasMore || false);
      
    } catch (err) {
      setError('Notification system is being set up. Please check back later.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [fetchNotifications]);

  // Load notifications when filter changes
  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Filter change handler removed since filters are no longer displayed

  /**
   * Handle mark all as read
   */
  const handleMarkAllRead = async () => {
    try {
      await markAllAsRead(null); // Mark all notifications as read
      
      // Refresh the current view
      await loadNotifications();
    } catch (error) {
      // Silently handle mark-all-read failure (toast shown by context)
    }
  };

  /**
   * Handle load more notifications
   */
  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      loadNotifications(notifications.length, true);
    }
  };

  /**
   * Handle notification click
   */
  const handleNotificationClick = (notification) => {
    onNotificationClick?.();
    
    // Navigate to action URL if available
    const actionUrl = notification.data?.actionUrl || notification.actionUrl;
    if (actionUrl) {
      navigate(actionUrl);
    }
  };

  /**
   * Handle view all notifications
   */
  const handleViewAll = () => {
    navigate('/notifications');
    onClose?.();
  };

  // Filter info removed since filters are no longer displayed

  return (
    <div className={styles.dropdown} role="dialog" aria-label="Notifications">
      {/* Header */}
      <div className={styles.header}>
        <h3 className={styles.title}>Notifications</h3>
        <div className={styles.headerActions}>
          {unreadCount > 0 && (
            <button
              className={styles.markAllRead}
              onClick={handleMarkAllRead}
              title="Mark all as read"
              aria-label="Mark all notifications as read"
            >
              <FaCheck /> Mark all read
            </button>
          )}
        </div>
      </div>

      {/* Filters removed for cleaner interface */}

      {/* Content */}
      <div className={styles.content} role="tabpanel">
        {loading ? (
          <div className={styles.loading}>
            <FaSpinner className={styles.spinner} />
            <span>Loading notifications...</span>
          </div>
        ) : error ? (
          <div className={styles.error}>
            <FaExclamationTriangle className={styles.errorIcon} />
            <span>{error}</span>
            <button 
              className={styles.retryButton}
              onClick={() => loadNotifications()}
            >
              Try Again
            </button>
          </div>
        ) : notifications.length === 0 ? (
          <div className={styles.empty}>
            <div className={styles.emptyIcon}>📭</div>
            <div className={styles.emptyTitle}>No notifications</div>
            <div className={styles.emptyMessage}>
              New notifications will appear here.
            </div>
          </div>
        ) : (
          <>
            {/* Notification List */}
            <div className={styles.notificationList}>
              {notifications.filter(notification => notification && notification._id).map(notification => (
                <NotificationItem
                  key={notification._id}
                  notification={notification}
                  onClick={() => handleNotificationClick(notification)}
                  onRead={() => loadNotifications()}
                  showCategory={false}
                />
              ))}
            </div>

            {/* Load More */}
            {hasMore && (
              <div className={styles.loadMore}>
                <button
                  className={styles.loadMoreButton}
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? (
                    <>
                      <FaSpinner className={styles.spinner} />
                      Loading...
                    </>
                  ) : (
                    'Load More'
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <button 
          className={styles.viewAllButton}
          onClick={handleViewAll}
        >
          View All Notifications
        </button>
        <button
          className={styles.refreshButton}
          onClick={refreshNotifications}
          title="Refresh notifications"
          aria-label="Refresh notifications"
        >
          <FaSync />
        </button>
      </div>
    </div>
  );
};

export default NotificationDropdown;
